import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Heading, Text } from '@bn-apps/design-system-ui-react';
import { Button, Container } from '@bn-apps/dim-ui-react';
import routes from '@config/routes';
import appStyles from '@/App.module.scss';

interface AccessDeniedProps {
    reason?: string;
    requiredLevel?: string;
}

/**
 * AccessDenied Component
 *
 * Displays when a user attempts to access a resource they don't have permission for.
 * Provides clear feedback and guidance on how to proceed.
 *
 * Accessibility Features:
 * - ARIA role="alert" for immediate screen reader announcement
 * - aria-describedby linking to detailed explanation
 * - Clear, descriptive error messages
 * - Keyboard-accessible actions
 */
const AccessDenied = ({ reason, requiredLevel = 'Project Member' }: AccessDeniedProps): JSX.Element => {
  const { t } = useTranslation();

  return (
      <Container className={appStyles.pageContent}>
        {/* Hero Section */}
        <div className="bn-mbe-2000">
          <Heading level={1} fontSize="display3xl" className="bn-mbe-1200">
            🔒 {t('auth.accessDenied.title')}
          </Heading>

          <Text fontSize="lg" className="bn-mbe-800">
            {reason || t('auth.accessDenied.message', { requiredLevel })}
          </Text>

          <div className="bn-mbe-1200">
            <Heading level={2} fontSize="md" className="bn-mbe-400">
              {t('auth.accessDenied.whyTitle')}
            </Heading>
            <Text className="bn-mbe-800">
              {t('auth.accessDenied.whyMessage', { requiredLevel })}
            </Text>

            <Heading level={2} fontSize="md" className="bn-mbe-400">
              {t('auth.accessDenied.whatToDoTitle')}
            </Heading>
            <ul>
              <li className="bn-mbe-200">
                <Text>{t('auth.accessDenied.contactAdmin')}</Text>
              </li>
              <li className="bn-mbe-200">
                <Text>{t('auth.accessDenied.verifyRole')}</Text>
              </li>
              <li className="bn-mbe-200">
                <Text>{t('auth.accessDenied.tryLogin')}</Text>
              </li>
              <li className="bn-mbe-200">
                <Text>
                  {t('help.contactPrompt')}{' '}
                  <a href={`mailto:${t('footer.supportEmail')}`}>{t('help.contactAction')}</a>
                </Text>
              </li>
            </ul>
          </div>

          <div className="bn-flex bn-gap-300">
            <Link to={routes.home}>
              <Button hierarchy="primary" size="md">
                {t('common.goHome')}
              </Button>
            </Link>
            <Link to={routes.help}>
              <Button hierarchy="secondary" size="md">
                {t('common.helpCenter')}
              </Button>
            </Link>
          </div>
      </div>
    </Container>
  );
};

export default AccessDenied;
